import React from "react";
import styles from "./Navigation.module.css";
import { SectionElement, SectionLink, SubSection } from "./types";
import { NavigationSubSection } from "./SubSection";

function BaseNavigationSection({
  icon,
  header,
  expanded,
  children,
  onMouseDown,
  onFocus,
}: SectionElement) {
  return (
    <div className={styles.navigationSection}>
      <div
        className={styles.navigationSectionHeader}
        role="button"
        tabIndex={0}
        onMouseDown={onMouseDown}
        onFocus={onFocus}
        data-testid={`navigation-${header.toLowerCase().replaceAll(" ", "-")}`}
      >
        <i className={`narmi-icon-${icon}`} />
        <div className={styles.navigationSectionHeaderText}>{header}</div>
        <i className={`narmi-icon-${expanded ? "chevron-up" : "chevron-down"}`} />
      </div>
      {children && (
        // to prevent re-initializing the children on open/close, the "expanded" prop controls the display value
        // rather than adding/removing the children from the DOM.
        <div
          className={styles.navigationSection}
          style={{ display: expanded ? undefined : "none" }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function NavigationSection({
  icon,
  header,
  overrideSingleChildHeader,
  children,
  expanded,
  compact = false,
  onMouseDown,
  onFocus,
}: SectionElement) {
  // if there are no children, render the menu item.
  if (children === undefined) {
    return (
      <BaseNavigationSection
        icon={icon}
        header={header}
        expanded={expanded}
        onMouseDown={onMouseDown}
        onFocus={onFocus}
      />
    );
  }

  // if the child is explicitly false, render nothing.
  if (children === false) {
    return null;
  }

  // Flatten grouped children so the counting below sees real links, not a nested array.
  const flatChildren = (Array.isArray(children) ? children : [children]).flat();

  // filter out any falsy children, that is, any NavLinks that return false
  // or any SubSections whose children all return false.
  const definedChildren = flatChildren.filter((child): child is SubSection | SectionLink => {
    if (!child) return false;
    if (child.type === NavigationSubSection) {
      const subChildren = Array.isArray(child.props.children)
        ? child.props.children
        : [child.props.children];
      return subChildren.some((subChild) => !!subChild);
    }
    return true;
  });

  // if there are no active children, render nothing.
  if (definedChildren.length === 0) {
    return null;
  }

  // if there is only one child, render it as a NavigationSection if it's a subsection or as a NavLink if it's a link
  if (definedChildren.length === 1) {
    if (definedChildren[0].type === NavigationSubSection) {
      return (
        <NavigationSection
          header={(definedChildren[0] as SubSection).props.header}
          children={(definedChildren[0] as SubSection).props.children}
          icon={icon}
          compact={compact}
          onMouseDown={onMouseDown}
          onFocus={onFocus}
          expanded={expanded}
          overrideSingleChildHeader={overrideSingleChildHeader}
        />
      );
    }

    const preservesIdentity = Boolean((definedChildren[0] as SectionLink).props.preserveIdentity);

    return React.cloneElement(definedChildren[0] as SectionLink, {
      // TODO: should we propagate the "new" badge here?
      icon: preservesIdentity ? (
        definedChildren[0].props.icon
      ) : (
        <i className={`${styles.navigationLinkIcon} narmi-icon-${icon}`} />
      ),
      compact,
      children:
        overrideSingleChildHeader && !preservesIdentity
          ? header
          : definedChildren[0].props.children,
    });
  }

  // in compact view, render the first child with this section's icon.
  if (compact) {
    if (definedChildren[0].type === NavigationSubSection)
      return (
        // if the first child is a SubSection, render it as a NavigationSection.
        // if the original SubSection has truthy children, the first child will then be rendered as a link
        // when the SubSection is rendered as a NavigationSection
        <NavigationSection
          {...(definedChildren[0] as SubSection).props}
          icon={icon}
          compact={compact}
        />
      );

    return React.cloneElement(definedChildren[0] as SectionLink, {
      icon: (definedChildren[0] as SectionLink).props.preserveIdentity ? (
        definedChildren[0].props.icon
      ) : (
        <i
          className={`${styles.navigationLinkIcon} narmi-icon-${icon}`}
          style={{ color: "#4c4c4c" }}
        />
      ),
      compact,
    });
  }

  return (
    <BaseNavigationSection
      icon={icon}
      header={header}
      expanded={expanded}
      onMouseDown={onMouseDown}
      onFocus={onFocus}
    >
      {definedChildren}
    </BaseNavigationSection>
  );
}
